/**
 * Hosts {@link ChargebackPanel} on the banking page.
 *
 * Connection-scoped for the same reason {@link RemittanceSection} is: a
 * chargeback we could not match to a home belongs to no association, so
 * there is no per-HOA screen it could appear on. This is the only place
 * it is visible.
 *
 * WHY IT IS A SEPARATE SECTION rather than a tab inside the remittance
 * panel: the two answer opposite questions. That one is "what came in",
 * this one is "what went back out", and a manager arrives looking for
 * one or the other. Folding them together would also make the empty
 * state ambiguous — "nothing here" would have to mean two things at
 * once.
 *
 * The connection picker is duplicated rather than lifted into shared
 * state. Two small copies of "load the connections, show the first"
 * cost less than a context that two sections have to agree about, and
 * the page has no other notion of a selected connection to hang it on.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Subheading } from './common/Catalyst/heading';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { ConnectionPicker } from './bank/linear/ConnectionPicker';
import { bankService } from './clients/ApiClients';
import { BankConnectionReturn } from './clients/rust-api-client';
import { getCompanyIdFromToken } from './utils/TokenUtils';
import { getBankErrorParts } from './utils/bankHelpers';
import ChargebackPanel from './ChargebackPanel';

const ChargebackSection: React.FC = () => {
  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const [connections, setConnections] = useState<BankConnectionReturn[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      const response = await bankService.listCompanyConnections({
        companyId,
      });
      const active = response.data.filter((c) => c.is_active);
      setConnections(active);
      setSelected((current) => current ?? active[0]?.id ?? null);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't load bank connections.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return null;

  if (error) {
    return <ErrorAlert className="mt-4">{error}</ErrorAlert>;
  }

  // No connection means no bank to take anything back, which is a
  // different fact from a bank that has taken nothing back.
  if (connections.length === 0) {
    return (
      <div className="mt-8">
        <Subheading>Returned payments</Subheading>
        <Text className="mt-1">
          No active bank connection, so there is nothing to return.
        </Text>
      </div>
    );
  }

  return (
    <div>
      {connections.length > 1 && (
        <div className="mt-8 max-w-sm">
          <Subheading>Returned payments for</Subheading>
          <ConnectionPicker
            className="mt-2 border border-line"
            connections={connections}
            value={selected}
            onChange={setSelected}
          />
        </div>
      )}
      {selected && <ChargebackPanel connectionId={selected} />}
    </div>
  );
};

export default ChargebackSection;
